import type { OffsetsFunction } from '@popperjs/core/lib/modifiers/offset';
import { NotificationInfoOutlineIcon } from '@tableau/icons/NotificationInfoOutlineIcon';
import * as React from 'react';
import { Popper } from 'react-popper';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { IconStyle } from '../../icons/Icons';
import { FocusVisibleIndicator } from '../../styles/FocusIndicators';
import { makeTestId } from '../../utils/Declarative';
import { generateId, isElementOverflownX } from '../../utils/DomUtil';
import { ElemAttrs } from '../../utils/HtmlAttrs';
import { Overlay } from '../../utils/Overlay';
import { assignRef, FCProps, mergeEventHandlers } from '../../utils/ReactHelpers';
import { StackingComponentProps, StackingComponentWrapper } from '../../utils/StackingComponent';
// eslint-disable-next-line max-len
import { adjustClientRectForOffset, AggregateTooltipPadding, DefaultMaxWidth, TooltipPlacementMap, TooltipPosition, TooltipPositionInfoMap, TooltipStyleName, TooltipTheme, TooltipThemeContext } from './TooltipConsts';

// eslint-disable-next-line deprecation/deprecation
export { TooltipPosition, TooltipThemeContext, TooltipTheme };

export interface TooltipProps extends Pick<StackingComponentProps, 'stackingRootId' | 'zIndex'> {
  /**
   * When true, the tooltip will slightly delay before showing for pointer interactions.
   */
  afterDelay?: boolean | number;

  /**
   * Defaults to an 18x18 "info" icon.
   *
   * The anchor element for the tooltip. Must accept and rest DOM attributes. For the simple case of plain text, wrap the text in a `<span>`.
   */
  children?: React.ReactElement<Pick<ElemAttrs,
    'aria-describedby' | 'onBlur' | 'onFocus' | 'onKeyUp' | 'onMouseEnter' | 'onMouseLeave' | 'onMouseMove' | 'tabIndex'
  >>;

  /** Set to true to render the tooltip against the cursor rather than the rendered children. */
  followCursor?: boolean;

  /**
   * @deprecated use `tooltipContent`, contact #tab-dev-shared-widgets if that's not possible.
   *
   * Called to get the tooltip content. Must return a `ReactElement`, i.e. it cannot return a plain string.
   *
   * **NOTE** this component is for static tooltip content only. It is not WCAG-compliant to put interactive content
   * inside the tooltip. For interactive content, use `PopoverWidget`.
   */
  getContent?: () => Promise<React.ReactElement> | React.ReactElement | null;

  /**
   * Beginning in v9.57, `Tooltip` was updated to use the same positioning logic as `PopoverWidget`.
   * Setting this prop to `true` will restore the previous behavior, but please contact us in #tab-dev-shared-widgets before using it.
   */
  legacyPositioning?: boolean;

  /**
   * The desired max width of the tooltip container.
   * @default 300
   */
  maxWidth?: number;

  /**
   * When true, tooltip will only display if the wrapped element's `clientWidth < scrollWidth`,
   * i.e. when it is horizontally overflowing.
   */
  onlyShowWhenClipped?: boolean;

  /**
   * The desired position that the tooltip will be displayed against the target.
   * NOTE: If there is not enough room for the tooltip at the desired position, we will attempt the opposite position.
   * @default TooltipPosition.Right
   */
  position?: TooltipPosition;

  /** Test id to be attached to the tooltip. */
  testId?: string;

  /**
   * The content that will be displayed inside the tooltip.
   *
   * **NOTE** this component is for static tooltip content only. It is not WCAG-compliant to put interactive content
   * inside the tooltip. For interactive content, use `PopoverWidget`.
   */
  tooltipContent?: React.ReactChild;
}

export interface TooltipState {
  /** The ClientRect that the tooltip overlay should anchor against. */
  anchorClientRect: ClientRect | undefined;

  /** The content to be displayed. */
  content: TooltipProps['tooltipContent'];

  /** The width of the tooltip. */
  width: number | undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isPromiseLike<T>(o: any): o is Promise<T> {
  return typeof o === 'object' && 'then' in o && typeof o.then === 'function';
}

export const InfoIcon: React.FC<Partial<FCProps<typeof NotificationInfoOutlineIcon>>> = (props) => {
  const { className, style, ...restProps } = props;
  return <NotificationInfoOutlineIcon
    aria-label={Messages.tooltipAnchorAlt()}
    color='#666'
    size={18}
    {...restProps}
    className={TypeStyle.classes(className, FocusVisibleIndicator)}
    style={{ ...IconStyle, ...style }}
  />;
};

/**
 * Wrapper which displays a popup when the child element receives focus or is hovered.
 *
 * **NOTE**: this component is for static tooltip content only. It is not WCAG-compliant to put interactive content
 * inside the tooltip. For interactive content, use `PopoverWidget`.
 *
 * @example
 *   <Tooltip tooltipContent='Last updated yesterday' />
 * @example
 *   <Tooltip tooltipContent='Some interesting info'>
 *     <span>Hover here for more info</span>
 *   </Tooltip>
 */
export class Tooltip extends React.Component<TooltipProps, TooltipState> {
  private readonly tooltipId: string = generateId();
  private delayTimeoutId: number | undefined = undefined;
  private tooltipContentElement: HTMLElement | null = null;

  public static readonly DelayMS: number = 250;

  public static defaultProps: Pick<TooltipProps, 'children' | 'maxWidth' | 'position'> = {
    children: <InfoIcon />,
    maxWidth: DefaultMaxWidth,
    position: TooltipPosition.Right,
  };

  /** Singleton pattern so that we don't end up with multiple tooltips showing at the same time */
  private static displayedTooltip?: { dismiss: () => void; tooltipId: string; };

  private static dismissDisplayedTooltip(): void {
    if (Tooltip.displayedTooltip) {
      const { dismiss } = Tooltip.displayedTooltip;
      Tooltip.displayedTooltip = undefined;

      try {
        dismiss();
      } catch (error) {
        console.warn({ message: 'failed to dismiss displayed tooltip', error });
      }
    }
  }

  public state: TooltipState = {
    anchorClientRect: undefined,
    content: undefined,
    width: undefined,
  };

  private clearTimeout = () => {
    window.clearTimeout(this.delayTimeoutId);
    this.delayTimeoutId = undefined;
  }

  public componentDidUpdate(prevProps: TooltipProps): void {
    // eslint-disable-next-line deprecation/deprecation
    if (this.state.content && (prevProps.getContent !== this.props.getContent) || (prevProps.tooltipContent !== this.props.tooltipContent)) {
      if (this.delayTimeoutId === undefined) { // don't suddenly show the tooltip if the prop changes during the delay period
        this.refreshContent();
      }
    }
  }

  public componentWillUnmount(): void {
    this.dismissTooltipOverlay();
  }

  private fixWidth = (): void => {
    if (this.state.content && this.state.anchorClientRect && this.tooltipContentElement && this.state.width === undefined) {
      this.setState({ width: this.tooltipContentElement.getBoundingClientRect().width + AggregateTooltipPadding });
    }
  }

  private handleTooltipOverlayContentRef = (element: HTMLElement | null): void => {
    this.tooltipContentElement = element ? element.firstElementChild as HTMLElement : null;
  }

  private handleFocus: React.FocusEventHandler<HTMLElement> = mergeEventHandlers(e => {
    if (!this.state.width) { // focus events don't have coordinates; make sure the tooltip doesn't jump if it's pointer-focused
      this.setAnchorClientRectFromEvent(e as unknown as React.MouseEvent<HTMLElement>);
    }
    this.showTooltipOverlay(e.currentTarget);
  }, e => (this.props.children as TooltipProps['children'])?.props.onFocus?.(e));

  private handleBlur: React.FocusEventHandler<HTMLElement> = mergeEventHandlers(() => {
    this.dismissTooltipOverlay();
  }, e => (this.props.children as TooltipProps['children'])?.props.onBlur?.(e));

  private handleKeyUp: React.KeyboardEventHandler<HTMLElement> = mergeEventHandlers(e => {
    if (this.state.content && this.state.anchorClientRect && e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.dismissTooltipOverlay();
    }
  }, e => (this.props.children as TooltipProps['children'])?.props.onKeyUp?.(e));

  private handleMouseEnter: React.MouseEventHandler<HTMLElement> = mergeEventHandlers(e => {
    if (this.state.width) { return; } // skip if already showing
    const { currentTarget } = e;

    this.setAnchorClientRectFromEvent(e); // do it now so the rect can update in subsequent mousemove events even before a delayed show

    if (this.props.afterDelay && this.delayTimeoutId === undefined) {
      // Semi-secret support for customizable delay time W-15523638
      const delayTime: number = typeof this.props.afterDelay === 'number' ? this.props.afterDelay : Tooltip.DelayMS;
      this.delayTimeoutId = window.setTimeout(() => this.showTooltipOverlay(currentTarget), delayTime);
    } else {
      this.showTooltipOverlay(currentTarget);
    }
  }, e => (this.props.children as TooltipProps['children'])?.props.onMouseEnter?.(e));

  private handleMouseMove: React.MouseEventHandler<HTMLElement> = mergeEventHandlers(e => {
    if (!this.props.followCursor) { return; }

    this.setAnchorClientRectFromEvent(e);
    if (!this.state.width && this.delayTimeoutId === undefined) {
      // show this tooltip even if another tooltip was shown (e.g. by a focus event) after the mouse entered
      this.handleMouseEnter(e);
    }
  }, e => (this.props.children as TooltipProps['children'])?.props.onMouseMove?.(e));

  private handleMouseLeave: React.MouseEventHandler<HTMLElement> = mergeEventHandlers(() => {
    this.dismissTooltipOverlay();
  }, e => (this.props.children as TooltipProps['children'])?.props.onMouseLeave?.(e));

  private showTooltipOverlay = (anchorElem: HTMLElement): void => {
    if (this.props.onlyShowWhenClipped && !isElementOverflownX(anchorElem)) {
      this.dismissTooltipOverlay();
      return;
    }

    if (Tooltip.displayedTooltip && Tooltip.displayedTooltip.tooltipId !== this.tooltipId) {
      Tooltip.dismissDisplayedTooltip();
    }
    Tooltip.displayedTooltip = { dismiss: this.dismissTooltipOverlay, tooltipId: this.tooltipId };

    this.refreshContent();
  }

  private refreshContent(): void {
    const setContent = (content: React.ReactChild) => {
      this.clearTimeout();
      this.setState({ content, width: undefined }, this.fixWidth);
    };

    // eslint-disable-next-line deprecation/deprecation
    const contentyThing = this.props.tooltipContent ?? this.props.getContent?.();
    if (!contentyThing) {
      this.dismissTooltipOverlay();
    } else if (isPromiseLike(contentyThing)) {
      contentyThing
        .then(setContent)
        .catch(error => {
          console.warn({ message: `'getContent' promise rejected`, error });
          this.dismissTooltipOverlay();
        });
    } else {
      setContent(contentyThing);
    }
  }

  private dismissTooltipOverlay = (): void => {
    if (this.tooltipId === Tooltip.displayedTooltip?.tooltipId) {
      Tooltip.displayedTooltip = undefined;
    }

    this.clearTimeout();
    this.setState({ anchorClientRect: undefined, content: undefined, width: undefined });
  }

  private setAnchorClientRectFromEvent({ clientX, clientY, currentTarget, type }: React.MouseEvent<HTMLElement>): void {
    this.setState({
      anchorClientRect: type === 'focus' || !this.props.followCursor ?
        adjustClientRectForOffset(currentTarget.getBoundingClientRect()) :
        adjustClientRectForOffset({
          bottom: clientY,
          height: 0,
          left: clientX,
          right: clientX,
          top: clientY,
          width: 0,
        })
    });
  }

  private getAnchorClientRect = (): ClientRect | undefined => this.state.anchorClientRect;

  private renderChildren(): React.ReactNode {
    const children = this.props.children!;
    return (
      React.cloneElement(React.Children.only<Required<TooltipProps>['children']>(children), {
        'aria-describedby': this.state.anchorClientRect ? this.tooltipId : undefined,
        onBlur: this.handleBlur,
        onFocus: this.handleFocus,
        onKeyUp: this.handleKeyUp,
        onMouseEnter: this.handleMouseEnter,
        onMouseLeave: this.handleMouseLeave,
        onMouseMove: this.handleMouseMove,
        tabIndex: children.props.tabIndex ?? 0,
      })
    );
  }

  private renderTooltipElement(style: React.CSSProperties, tooltipElementRef?: React.Ref<HTMLElement>): React.ReactNode {
    const { content, width } = this.state;
    const maxWidth = this.props.maxWidth!;
    /**
     * In order to have the content wrapper grow to fit the tooltip contents, we initially render the wrapper with a width of maxWidth.
     * Once the tooltip content is rendered, we get a reference to the content element and get the actual rendered width. This width is
     * then used to calculate the width of the content wrapper. This results in a content wrapper that fits to the tooltip content.
     */
    const contentWrapperStyle: React.CSSProperties = {
      visibility: width === undefined ? 'hidden' : 'visible',
      width: Math.ceil(width ?? maxWidth), // TFS 826030 round up to nearest int to avoid layout errors in Safari
    };

    return <TooltipThemeContext.Consumer>{tooltipTheme => // eslint-disable-line deprecation/deprecation
      <div
        className={TooltipStyleName}
        data-tb-test-id={this.props.testId}
        id={this.tooltipId}
        ref={ref => {
          assignRef(tooltipElementRef, ref);
          this.handleTooltipOverlayContentRef(ref)
        }}
        role='tooltip'
        style={{ ...contentWrapperStyle, ...tooltipTheme, ...style }}
      >
        {React.isValidElement(content) ? content : <span>{content}</span>}
      </div>
    }</TooltipThemeContext.Consumer>; // eslint-disable-line deprecation/deprecation
  }

  private renderOverlayTooltip(): React.ReactNode {
    const { anchorOrigin, stackedOrigin } = TooltipPositionInfoMap[this.props.position!];
    return (
      <Overlay
        allowAutoPosition
        anchorOrigin={anchorOrigin}
        disablePointerEvents={true}
        getAnchorClientRect={this.getAnchorClientRect}
        shouldAutoPositionOnScrollingRootScroll={this.props.followCursor}
        stackedOrigin={stackedOrigin}
        stackingRootId={this.props.stackingRootId} // eslint-disable-line deprecation/deprecation
        zIndex={this.props.zIndex}
      >
        {this.renderTooltipElement({})}
      </Overlay>
    );
  }

  private renderPopperTooltip(): React.ReactNode {
    const popperOffsetFn: OffsetsFunction = (data) => {
      /** if/when we delete the legacy Overlay mode, `distance` can probably replace `adjustClientRectForOffset` */
      const distance = 0;
      /** in corners, adjust the default positioning so we end up "kitty corner" rather than "across the street" */
      const skidding = ({ start: -data.popper.width, end: data.popper.width, undefined: 0 })[data.placement.split('-')[1]];
      return [skidding, distance];
    };

    return (
      <Popper
        modifiers={[
          { name: 'eventListeners', options: { resize: true, scroll: false } }, // without this the tooltip moves with scrolling, i.e. detaches from the trigger
          { name: 'offset', options: { offset: popperOffsetFn } },
          { name: 'preventOverflow', options: { altAxis: true } }, // allow tooltip to overlap the trigger element if not enough space on either side
        ]}
        placement={TooltipPlacementMap[this.props.position!]}
        referenceElement={{ getBoundingClientRect: () => this.state.anchorClientRect! }}
      >{popperProps =>
        <StackingComponentWrapper
          stackingRootId={this.props.stackingRootId} // eslint-disable-line deprecation/deprecation
          wrapperTestId={makeTestId(this.props.testId, 'Tooltip')}
          zIndex={this.props.zIndex}
        >
          {this.renderTooltipElement(popperProps.style, popperProps.ref)}
        </StackingComponentWrapper>}
      </Popper>
    );
  }

  public render(): React.ReactNode {
    const tooltipElement = (() => {
      if (!this.state.content || !this.state.anchorClientRect) {
        return undefined;
      } else if (this.props.legacyPositioning) {
        return this.renderOverlayTooltip();
      } else {
        return this.renderPopperTooltip();
      }
    })();

    return <>
      {this.renderChildren()}
      {tooltipElement}
    </>;
  }
}
